import React from 'react';
import { PRODUCT_MOCKUP, PRODUCT_MOCKUP_ALT } from '../data/product';

/** Small stick-figure gymnast in a star shape (flat line style). */
function StarGymnast({
  x,
  y,
  s = 1,
  color = '#16213E'
}: {
  x: number;
  y: number;
  s?: number;
  color?: string;
}) {
  return <g transform={`translate(${x} ${y}) scale(${s})`} stroke={color} strokeWidth="3.2" strokeLinecap="round" fill="none" data-id="element-254" data-mp="3shx9e66vg514">
      <circle cx="0" cy="-26" r="6" fill={color} stroke="none" data-id="element-255" data-mp="o1mg4tru3gq2" />
      <path d="M0 -18 L0 4" data-id="element-256" data-mp="o3fme7yjbyxj" />
      <path d="M0 -14 L-18 -26 M0 -14 L18 -26" data-id="element-257" data-mp="t0604omvaifs" />
      <path d="M0 4 L-14 24 M0 4 L14 24" data-id="element-258" data-mp="szxpl3e7vp7r" />
    </g>;
}

/**
 * Shows the uploaded mockup when PRODUCT_MOCKUP is set; otherwise draws the
 * cover in code: Cloud page, apparatus colour strip, cartwheel trajectory.
 */
export function ProductVisual({
  className = '',
  lazy = false
}: {
  className?: string;
  lazy?: boolean;
}) {
  if (PRODUCT_MOCKUP) {
    return <img src={PRODUCT_MOCKUP} alt={PRODUCT_MOCKUP_ALT} loading={lazy ? 'lazy' : undefined} className={`mx-auto aspect-square w-full rounded-3xl object-cover shadow-book ring-4 ring-white/10 ${className}`} data-id="element-259" data-mp="1brgmqtlk891v" />;
  }
  return <div className={`relative mx-auto ${className}`} role="img" aria-label={PRODUCT_MOCKUP_ALT} data-id="element-260" data-mp="1zbf2r3bzd9v6">
      {/* the other volumes, fanned behind */}
      <div className="absolute inset-0 translate-x-[9%] translate-y-[2%] rotate-[6deg] rounded-[14px] bg-amber shadow-book" aria-hidden="true" data-id="element-261" data-mp="29aedcrgb3fkt" />
      <div className="absolute inset-0 translate-x-[6%] translate-y-[1.5%] rotate-[4deg] rounded-[14px] bg-teal shadow-book" aria-hidden="true" data-id="element-262" data-mp="2n4x76ei6pari" />
      <div className="absolute inset-0 translate-x-[3%] translate-y-[1%] rotate-[2deg] rounded-[14px] bg-indigo shadow-book" aria-hidden="true" data-id="element-263" data-mp="2x1a21fpgtunu" />

      <div style={{
      containerType: 'inline-size'
    }} className="relative aspect-[4/3] overflow-hidden rounded-[14px] bg-white shadow-book" data-id="element-264" data-mp="2rzudverzr818">
        <span className="apparatus-strip absolute inset-x-0 top-0 h-[3.2%]" aria-hidden="true" data-id="element-265" data-mp="xoxs6grvwr3y" />
        <div className="relative px-[7%] pt-[8%]" data-id="element-266" data-mp="2xr7264etktmw">
          <p className="font-display text-[3cqw] font-bold uppercase tracking-[0.2em] text-coral-deep" data-id="element-267" data-mp="2xsi7i8nwy9g4">
            A full year · 52 weekly classes
          </p>
          <p className="mt-[2cqw] font-display text-[8.6cqw] font-extrabold leading-[0.95] tracking-tight text-ink" data-id="element-268" data-mp="36pad66s6i80g">
            Rec Gymnastics
            <br data-id="element-269" data-mp="3gp1829knip12" />
            Class-Ready
          </p>
          <p className="mt-[2.4cqw] max-w-[62%] font-sans text-[3.1cqw] font-medium leading-snug text-slate" data-id="element-270" data-mp="3goi1fp0gbn2c">
            Done-for-You Lesson Plans &amp; Skill Progressions
          </p>
        </div>

        {/* floor lane + cartwheel trajectory */}
        <svg viewBox="0 0 400 300" className="absolute inset-0 h-full w-full" aria-hidden="true" data-id="element-271" data-mp="3ug6blmr541go">
          <rect x="0" y="238" width="400" height="62" fill="#F4F6FB" data-id="element-272" data-mp="3add14zdyb5p" />
          <line x1="24" y1="258" x2="376" y2="258" stroke="#FF5A5F" strokeWidth="3" strokeDasharray="2 9" strokeLinecap="round" data-id="element-273" data-mp="380m42bngqgi" />
          <path d="M196 250 C 240 150, 330 150, 362 250" stroke="#2541B2" strokeWidth="2.4" strokeDasharray="6 7" fill="none" data-id="element-274" data-mp="icvb6h2gvnjx" />
          <circle cx="262" cy="258" r="5" fill="#FF5A5F" data-id="element-275" data-mp="dd0f7z3u47s0" />
          <circle cx="290" cy="258" r="5" fill="#FF5A5F" data-id="element-276" data-mp="8ez1ltp6h0nx" />
          <circle cx="318" cy="258" r="5" fill="#17B8A6" data-id="element-277" data-mp="8fh252ivi11n" />
          <circle cx="346" cy="258" r="5" fill="#17B8A6" data-id="element-278" data-mp="4dvhgv03kigw" />
          <StarGymnast x={300} y={196} s={1.25} data-id="element-279" data-mp="4fgh2z19qrlm" />
          {/* level dots */}
          <g transform="translate(28 222)" data-id="element-280" data-mp="1134n4io22tot">
            <circle cx="0" cy="0" r="5" fill="#17B8A6" data-id="element-281" data-mp="w4tbq3i44b4h" />
            <circle cx="16" cy="0" r="5" fill="#FFC53D" data-id="element-282" data-mp="w5bg5nexhvnh" />
            <circle cx="32" cy="0" r="5" fill="#FF5A5F" data-id="element-283" data-mp="rdbcbjvqmilm" />
          </g>
          <text x="28" y="282" fill="#5A6072" fontFamily="Montserrat, sans-serif" fontWeight="700" fontSize="11" letterSpacing="2" data-id="element-284" data-mp="nc7uiru4qchr">
            FLOOR · BARS · BEAM · VAULT
          </text>
        </svg>
      </div>
    </div>;
}